/**
 * Platform-agnostic authentication service
 * Handles both web and React Native authentication flows
 */

import { isWeb, isReactNative } from '../utils/platform';
const shouldUseAuthEmulator =
  process.env.REACT_APP_USE_AUTH_EMULATOR === 'true' ||
  process.env.NODE_ENV === 'development';

const firebaseConfigFallback = (authInstance) => ({
  apiKey: authInstance?.app?.options?.apiKey || 'demo-key',
  authDomain: authInstance?.app?.options?.authDomain || 'localhost',
  projectId: authInstance?.app?.options?.projectId || 'demo-project',
});

/**
 * Sign in with Google - Platform agnostic
 * Automatically uses the correct method based on platform
 */
export const signInWithGoogle = async (auth) => {
  if (isWeb()) {
    return await signInWithGoogleWeb(auth);
  } else if (isReactNative()) {
    return await signInWithGoogleNative(auth);
  } else {
    throw new Error('Unsupported platform for authentication');
  }
};

/**
 * Web Google Sign-In (current implementation)
 */
const signInWithGoogleWeb = async (auth) => {
  const { signInWithPopup, GoogleAuthProvider } = require('firebase/auth');
  
  try {
    if (shouldUseAuthEmulator) {
      // Under the emulator, use the compat popup flow to restore the legacy hosted UI.
      const firebaseCompatModule = require('firebase/compat/app');
      const firebaseCompat = firebaseCompatModule.default || firebaseCompatModule;
      require('firebase/compat/auth');

      if (!firebaseCompat.apps.length) {
        firebaseCompat.initializeApp(auth.app?.options || firebaseConfigFallback(auth));
      }

      const compatAuth = firebaseCompat.auth();
      try {
        compatAuth.useEmulator('http://localhost:9099', { disableWarnings: true });
      } catch (err) {
        // ignore if already configured
      }

      const compatProvider = new firebaseCompat.auth.GoogleAuthProvider();
      compatProvider.setCustomParameters({ prompt: 'select_account' });

      console.log('[AUTH] Starting Google sign-in via compat popup', {
        usingEmulator: true,
        projectId: compatAuth.app?.options?.projectId,
        authDomain: compatAuth.app?.options?.authDomain,
        providerId: compatProvider?.providerId,
        providerCtor: compatProvider?.constructor?.name,
      });

      const result = await compatAuth.signInWithPopup(compatProvider);
      console.log('[AUTH] Compat Google sign-in popup resolved', {
        userUid: result?.user?.uid,
        providerId: result?.providerId,
        credentialProviderId: result?.credential?.providerId,
      });

      return {
        success: true,
        user: result.user,
        credential: result.credential
      };
    }

    const provider = new GoogleAuthProvider();
    if (!provider.providerId) {
      // Ensure providerId is populated for emulator compatibility
      provider.providerId = GoogleAuthProvider.PROVIDER_ID || 'google.com';
    }
    console.log('[AUTH] Starting Google sign-in via popup', {
      usingEmulator: shouldUseAuthEmulator,
      projectId: auth.app?.options?.projectId,
      authDomain: auth.app?.options?.authDomain,
      providerId: provider?.providerId,
      providerCtor: provider?.constructor?.name,
      providerScopes: provider?.getScopes ? provider.getScopes() : undefined,
    });
    const result = await signInWithPopup(auth, provider);
    console.log('[AUTH] Google sign-in popup resolved', {
      userUid: result?.user?.uid,
      providerId: result?.providerId,
      credentialProviderId: result?.credential?.providerId,
    });
    return {
      success: true,
      user: result.user,
      credential: result.credential
    };
  } catch (error) {
    console.error('[AUTH] Google sign-in popup failed', {
      code: error?.code,
      message: error?.message,
      customData: error?.customData,
      fullError: error
    });
    // Handle popup closed - try redirect as fallback
    if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/popup-blocked') {
      const { signInWithRedirect } = require('firebase/auth');
      try {
        const provider = new GoogleAuthProvider();
        await signInWithRedirect(auth, provider);
        return {
          success: true,
          redirect: true // Indicates redirect flow
        };
      } catch (redirectError) {
        throw new Error('Popup blocked. Please allow popups or try again.');
      }
    }
    throw error;
  }
};

/**
 * React Native Google Sign-In
 * Uses native Google Sign-In SDK
 */
const signInWithGoogleNative = async (auth) => {
  try {
    // Dynamically import React Native Google Sign-In
    // Use Function constructor to prevent webpack from analyzing the require
    // This ensures React Native packages are never bundled in web builds
    let GoogleSignin;
    try {
      // eslint-disable-next-line no-new-func
      const requireModule = new Function('moduleName', 'return require(moduleName)');
      GoogleSignin = requireModule('@react-native-google-signin/google-signin').GoogleSignin;
    } catch (e) {
      throw new Error('Google Sign-In SDK not installed. Please install @react-native-google-signin/google-signin');
    }
    
    const { signInWithCredential, GoogleAuthProvider } = require('firebase/auth');
    
    // Configure Google Sign-In (should be done at app initialization, but here as fallback)
    if (!GoogleSignin.isSignedIn) {
      await GoogleSignin.hasPlayServices();
    }
    
    // Get user info from Google (verify sign-in succeeded)
    await GoogleSignin.signIn();
    
    // Get Google ID token
    const { idToken } = await GoogleSignin.getTokens();
    
    // Create Firebase credential
    const googleCredential = GoogleAuthProvider.credential(idToken);
    
    // Sign in with Firebase
    const result = await signInWithCredential(auth, googleCredential);
    
    return {
      success: true,
      user: result.user,
      credential: googleCredential
    };
  } catch (error) {
    console.error('Native Google Sign-In error:', error);
    
    // Handle different error types
    if (error.code === 'sign_in_cancelled') {
      throw new Error('Sign-in was cancelled. Please try again.');
    } else if (error.code === 'IN_PROGRESS') {
      throw new Error('Sign-in is already in progress. Please wait.');
    } else if (error.code === 'PLAY_SERVICES_NOT_AVAILABLE') {
      throw new Error('Google Play Services not available. Please update your device.');
    }
    
    throw error;
  }
};

/**
 * Sign out - Platform agnostic
 */
export const signOut = async (auth, storage) => {
  const { signOut: firebaseSignOut } = require('firebase/auth');
  
  try {
    // Sign out from Firebase
    await firebaseSignOut(auth);
    
    // Clear storage based on platform
    if (isWeb()) {
      // Web: clear localStorage
      if (storage && storage.removeItem) {
        storage.removeItem('firebaseIdToken');
      }
    } else if (isReactNative()) {
      // React Native: clear AsyncStorage
      // Use Function constructor to prevent webpack from analyzing the require
      try {
        // eslint-disable-next-line no-new-func
        const requireModule = new Function('moduleName', 'return require(moduleName)');
        const AsyncStorage = requireModule('@react-native-async-storage/async-storage').default;
        await AsyncStorage.removeItem('firebaseIdToken');
      } catch (e) {
        // AsyncStorage not available (web environment during build)
        // This is fine - storage abstraction will handle it
        console.warn('AsyncStorage not available:', e);
      }
    }
    
    // If using native Google Sign-In, sign out from there too
    if (isReactNative() && !shouldUseAuthEmulator) {
      try {
        // eslint-disable-next-line no-new-func
        const requireModule = new Function('moduleName', 'return require(moduleName)');
        const { GoogleSignin } = requireModule('@react-native-google-signin/google-signin');
        await GoogleSignin.signOut();
      } catch (error) {
        if (!error.message?.includes('Cannot find module')) {
          console.warn('Google Sign-In sign out error:', error);
        }
      }
    }
    
    return { success: true };
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
};

/**
 * Get Firebase ID token - Platform agnostic
 */
export const getIdToken = async (user) => {
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  try {
    return await user.getIdToken();
  } catch (error) {
    console.error('Error getting ID token:', error);
    throw error;
  }
};

/**
 * Configure Google Sign-In for React Native
 * Should be called once at app initialization
 */
export const configureGoogleSignIn = () => {
  if (isReactNative()) {
    try {
      // eslint-disable-next-line no-new-func
      const requireModule = new Function('moduleName', 'return require(moduleName)');
      const { GoogleSignin } = requireModule('@react-native-google-signin/google-signin');
      
      // Get config from environment or config file
      // In production, these should come from your Firebase config
      const webClientId = process.env.REACT_NATIVE_GOOGLE_WEB_CLIENT_ID || 
                         process.env.REACT_APP_FIREBASE_GOOGLE_WEB_CLIENT_ID;
      
      GoogleSignin.configure({
        webClientId: webClientId, // Required for iOS
        iosClientId: process.env.REACT_NATIVE_GOOGLE_IOS_CLIENT_ID,
        offlineAccess: true, // Required for refresh token
      });
      
      return true;
    } catch (error) {
      console.error('Error configuring Google Sign-In:', error);
      return false;
    }
  }
  // Web doesn't need this configuration
  return true;
};

const authService = {
  signInWithGoogle,
  signOut,
  getIdToken,
  configureGoogleSignIn
};

export default authService;

